import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLocalAnalytics } from './useLocalAnalytics';

// Types
export interface DailyDataPoint {
  date: string;
  actual_output_mwh: number;
  expected_output_mwh: number;
  ratio: number;
  cloud_cover_avg: number;
  capacity_factor: number;
  temperature_avg?: number;
  sun_hours?: number;
}

export interface PeriodStats {
  period: string;
  start_date: string;
  end_date: string;
  total_days: number;
  total_actual_mwh: number;
  total_expected_mwh: number;
  avg_actual_mwh: number;
  avg_expected_mwh: number;
  avg_ratio: number;
  deficit_mwh: number;
  avg_cloud_cover: number;
  avg_capacity_factor: number;
  max_actual_mwh: number;
  min_actual_mwh: number;
  max_peak_power: number;
  avg_homes_powered: number;
}

export interface ThresholdStats {
  below_threshold: number;
  total_days: number;
  threshold: number;
  percentage: number;
}

export interface AnalyticsData {
  generated_at: string;
  data_range: {
    start: string;
    end: string;
    requested_days: number;
  };
  record_count: number;
  daily_data: DailyDataPoint[];
  weekly: PeriodStats;
  monthly: PeriodStats;
  quarterly: PeriodStats;
  six_month?: PeriodStats;
  annual?: PeriodStats;
  below_50_percent: ThresholdStats;
  below_20_percent: ThresholdStats;
  insights: {
    summary: string;
    performance_rating: string;
    key_findings: string[];
    recommendation: string;
  };
}

export interface AnalyticsFilters {
  dateRange: {
    start: Date;
    end: Date;
  };
  threshold: number; // Hard-coded to 0.7
  period: 'weekly' | 'monthly' | 'quarterly' | 'six_month' | 'annual';
}

export interface BackfillStatus {
  is_running: boolean;
  status: string;
  progress: number;
  total_days: number;
  record_count: number;
}

// Date range presets (removed - no longer used)

export function useAnalytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [backfillStatus, setBackfillStatus] = useState<BackfillStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AnalyticsFilters>({
    dateRange: {
      start: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
      end: new Date()
    },
    threshold: 0.7,
    period: 'monthly'
  });

  const { isInitialized: localDBReady, summary: localSummary, storeData, getData } = useLocalAnalytics();

  // Fetch analytics from API
  const fetchAnalytics = useCallback(async (days: number = 90) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get<AnalyticsData>(`/api/analytics?days=${days}`);
      const apiData = response.data;

      // Store in local DB
      if (localDBReady) {
        try {
          const dbData = apiData.daily_data.map(day => ({
            date: day.date,
            project_id: 'east-pye',
            actual_output_mwh: day.actual_output_mwh,
            expected_output_mwh: day.expected_output_mwh,
            peak_power_mw: 0, // Not available in this format
            nameplate_capacity_mw: 0, // Not available in this format
            cloud_cover_avg: day.cloud_cover_avg,
            sun_hours: day.sun_hours || 0,
            temperature_avg: day.temperature_avg || 0,
            capacity_factor: day.capacity_factor,
            homes_powered: Math.round(day.actual_output_mwh * 1000 / 11), // Estimate
            stored_at: new Date().toISOString()
          }));
          await storeData(dbData);
        } catch (dbError) {
          console.warn('Failed to store data locally:', dbError);
        }
      }

      setData(apiData);
    } catch (err) {
      console.error('API fetch failed, trying local data:', err);

      // Fallback to local data
      if (localDBReady) {
        try {
          const localData = await getData(
            filters.dateRange.start.toISOString().split('T')[0],
            filters.dateRange.end.toISOString().split('T')[0]
          );

          if (localData.length > 0) {
            // Convert local data to API format
            const convertedData = convertLocalDataToAnalytics(localData);
            setData(convertedData);
            setError('Using cached data (API unavailable)');
          } else {
            setError('No analytics data available');
          }
        } catch (localError) {
          setError('Failed to load analytics data from API and local storage');
        }
      } else {
        setError('Failed to load analytics data');
      }
    } finally {
      setLoading(false);
    }
  }, [localDBReady, storeData, getData, filters.dateRange]);

  // Fetch backfill status
  const fetchBackfillStatus = useCallback(async () => {
    try {
      const response = await axios.get<BackfillStatus>('/api/analytics/status');
      setBackfillStatus(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, []);

  // Trigger backfill
  const triggerBackfill = useCallback(async (days: number = 90, force: boolean = false) => {
    try {
      setError(null);
      await axios.post(`/api/analytics/backfill?days=${days}&force=${force}`);

      // Poll for completion
      const pollInterval = setInterval(async () => {
        const status = await fetchBackfillStatus();
        if (status && !status.is_running) {
          clearInterval(pollInterval);
          await fetchAnalytics(days);
        }
      }, 2000);

      // Initial status update
      await fetchBackfillStatus();
    } catch (err) {
      setError('Failed to start backfill');
    }
  }, [fetchBackfillStatus, fetchAnalytics]);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<AnalyticsFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Date preset functionality removed

  // Get filtered data based on current filters
  const filteredData = useMemo(() => {
    if (!data) return null;

    // Filter daily data by date range
    const filteredDaily = data.daily_data.filter(day => {
      const dayDate = new Date(day.date);
      return dayDate >= filters.dateRange.start && dayDate <= filters.dateRange.end;
    });

    // Recalculate stats for custom threshold
    const belowThreshold = filteredDaily.filter(day => day.ratio < filters.threshold).length;
    const customThresholdStats: ThresholdStats = {
      below_threshold: belowThreshold,
      total_days: filteredDaily.length,
      threshold: filters.threshold,
      percentage: filteredDaily.length > 0 ? (belowThreshold / filteredDaily.length) * 100 : 0
    };

    return {
      ...data,
      daily_data: filteredDaily,
      custom_threshold: customThresholdStats
    };
  }, [data, filters]);

  // Get period stats based on selected period
  const periodStats = useMemo(() => {
    if (!filteredData) return null;

    switch (filters.period) {
      case 'weekly': return filteredData.weekly;
      case 'monthly': return filteredData.monthly;
      case 'quarterly': return filteredData.quarterly;
      case 'six_month': return filteredData.six_month;
      case 'annual': return filteredData.annual;
      default: return filteredData.monthly;
    }
  }, [filteredData, filters.period]);

  // Initial load
  useEffect(() => {
    if (localDBReady) {
      fetchAnalytics(365);
      fetchBackfillStatus();
    }
  }, [localDBReady, fetchAnalytics, fetchBackfillStatus]);

  return {
    // Data
    data: filteredData,
    rawData: data,
    periodStats,
    backfillStatus,
    localSummary,

    // State
    loading,
    error,
    filters,
    localDBReady,

    // Actions
    fetchAnalytics,
    triggerBackfill,
    updateFilters
  };
}

// Helper function to convert local DB data to analytics format
function convertLocalDataToAnalytics(localData: any[]): AnalyticsData {
  const dailyData: DailyDataPoint[] = localData.map(item => ({
    date: item.date,
    actual_output_mwh: item.actual_output_mwh,
    expected_output_mwh: item.expected_output_mwh,
    ratio: item.expected_output_mwh > 0 ? item.actual_output_mwh / item.expected_output_mwh : 0,
    cloud_cover_avg: item.cloud_cover_avg,
    capacity_factor: item.capacity_factor,
    temperature_avg: item.temperature_avg,
    sun_hours: item.sun_hours
  }));

  // Calculate basic stats (simplified)
  const totalActual = dailyData.reduce((sum, d) => sum + d.actual_output_mwh, 0);
  const totalExpected = dailyData.reduce((sum, d) => sum + d.expected_output_mwh, 0);
  const avgRatio = totalExpected > 0 ? totalActual / totalExpected : 0;

  const basicStats: PeriodStats = {
    period: 'filtered',
    start_date: dailyData[0]?.date || '',
    end_date: dailyData[dailyData.length - 1]?.date || '',
    total_days: dailyData.length,
    total_actual_mwh: totalActual,
    total_expected_mwh: totalExpected,
    avg_actual_mwh: totalActual / dailyData.length,
    avg_expected_mwh: totalExpected / dailyData.length,
    avg_ratio: avgRatio,
    deficit_mwh: totalExpected - totalActual,
    avg_cloud_cover: dailyData.reduce((sum, d) => sum + d.cloud_cover_avg, 0) / dailyData.length,
    avg_capacity_factor: dailyData.reduce((sum, d) => sum + d.capacity_factor, 0) / dailyData.length,
    max_actual_mwh: Math.max(...dailyData.map(d => d.actual_output_mwh)),
    min_actual_mwh: Math.min(...dailyData.map(d => d.actual_output_mwh)),
    max_peak_power: 0, // Not available
    avg_homes_powered: Math.round((totalActual / dailyData.length) * 1000 / 11)
  };

  return {
    generated_at: new Date().toISOString(),
    data_range: {
      start: basicStats.start_date,
      end: basicStats.end_date,
      requested_days: dailyData.length
    },
    record_count: dailyData.length,
    daily_data: dailyData,
    weekly: basicStats,
    monthly: basicStats,
    quarterly: basicStats,
    below_50_percent: {
      below_threshold: dailyData.filter(d => d.ratio < 0.5).length,
      total_days: dailyData.length,
      threshold: 0.5,
      percentage: (dailyData.filter(d => d.ratio < 0.5).length / dailyData.length) * 100
    },
    below_20_percent: {
      below_threshold: dailyData.filter(d => d.ratio < 0.2).length,
      total_days: dailyData.length,
      threshold: 0.2,
      percentage: (dailyData.filter(d => d.ratio < 0.2).length / dailyData.length) * 100
    },
    insights: {
      summary: `Local data: ${dailyData.length} days, ${(avgRatio * 100).toFixed(1)}% average performance`,
      performance_rating: avgRatio > 0.8 ? 'good' : avgRatio > 0.6 ? 'moderate' : 'poor',
      key_findings: [
        `${dailyData.filter(d => d.ratio < 0.5).length} days below 50% capacity`,
        `Average cloud cover: ${basicStats.avg_cloud_cover.toFixed(1)}%`
      ],
      recommendation: 'Connect to API for full analytics'
    }
  };
}
